import { CustomerGroupType } from '@navio/nsdk';
import {
  BOOKING_CANCELLATION_SOURCE_OPTIONS,
  BOOKING_EVENT_SOURCE_OPTIONS,
  BOOKING_EVENT_TYPE_OPTIONS,
  CUSTOMER_SEGMENT_OPTIONS
} from 'noddi-util';
import { DROP_IN_DESCRIPTION } from '../../../features/Booking/BookingBadge';
import { CREATED_AT_FILTER_TEXT } from '../Date/useCreatedAtFilter';
import { UPDATED_AT_FILTER_TEXT } from '../Date/useUpdatedAtFilter';
import { ORDER_PAYMENT_STATUS_FILTER_OPTIONS } from '../Order/useOrderStatusFilters';
import { ORGANIZATION_TYPE_FILTER_OPTIONS } from '../Organization/useOrganizationTypeFilter';
import { CUSTOMER_GROUP_TYPE_FILTER_OPTIONS } from '../UserGroup/useCustomerGroupTypeFilter';
import {
  DATA_QUALITY_ISSUE_TYPES_FILTER,
  HAS_DATA_QUALITY_ISSUES_FILTER_TEXT
} from '../UserGroup/useUserGroupDataQualityFilters';
import { bookingFilterUnit } from './bookingFilterSet';
import { BOOKING_LOCATION_TYPE_FILTER_OPTIONS } from './useBookingLocationTypeFilter';
import { PAYMENT_OPTION_FILTER_OPTIONS } from './usePaymentOptionsFilter';

// Booking filters with static options: plain specs, shared by every booking list. Option-loading ones are in
// `useBookingFilterSpecs`.

export const bookingCancellationSourceFilter = bookingFilterUnit.multiSelect('cancellationSources', {
  title: 'Cancelled by',
  description:
    'Who or what cancelled the booking. System is an automatic cancel, such as an unpaid checkout or a fleet schedule change. Adapter is a cancel received from an external system.',
  options: BOOKING_CANCELLATION_SOURCE_OPTIONS,
  exclude: true
});

export const bookingFlagFilters = {
  isPickupBooking: bookingFilterUnit.boolean('isPickupBooking', {
    title: 'Pick up Booking',
    description: 'Specifies if the booking is a wheel pickup.'
  }),
  isDropIn: bookingFilterUnit.boolean('isDropIn', { title: 'Drop-in', description: DROP_IN_DESCRIPTION }),
  isFleetScheduleBooking: bookingFilterUnit.boolean('isFleetScheduleBooking', {
    title: 'Fleet booking',
    description: 'Bookings linked to a fleet schedule.'
  })
};

export const bookingCustomerGroupTypesFilter = bookingFilterUnit.multiSelect('customerGroupTypes', {
  title: 'Customer Group Types',
  description: 'Filter by customer group type',
  options: CUSTOMER_GROUP_TYPE_FILTER_OPTIONS,
  defaults: [CustomerGroupType.PERSONAL, CustomerGroupType.GROUP, CustomerGroupType.ORGANIZATION],
  exclude: true
});

export const bookingSegmentFilter = bookingFilterUnit.multiSelect('customerSegments', {
  title: 'Customer segment',
  description:
    'Lifecycle and loyalty segments: prospects (no bookings), new (1 completed), active clients (2-9), VIP (10+ or high spend), at risk / churned by recency, and fleet (3+ vehicles). All includes everyone.',
  options: CUSTOMER_SEGMENT_OPTIONS,
  exclude: true
});

export const bookingLocationTypeFilter = bookingFilterUnit.multiSelect('bookingLocationTypes', {
  title: 'Location type',
  description:
    'Where the service is performed. Mobile means a worker visits the customer, stationary means the customer visits a workshop.',
  options: BOOKING_LOCATION_TYPE_FILTER_OPTIONS,
  exclude: true
});

export const bookingPaymentOptionsFilter = bookingFilterUnit.multiSelect('paymentOptions', {
  title: 'Payment option',
  description: 'How the customer pays for the booking (e.g. pay before, pay after, invoice).',
  options: PAYMENT_OPTION_FILTER_OPTIONS,
  exclude: true
});

export const bookingOrderPaymentStatusesFilter = bookingFilterUnit.multiSelect('orderPaymentStatuses', {
  title: 'Order payment status',
  description: 'Filter by the order payment status (e.g., Paid, Unpaid, Refunded)',
  options: ORDER_PAYMENT_STATUS_FILTER_OPTIONS,
  exclude: true
});

export const bookingEventFilters = {
  eventTypes: bookingFilterUnit.multiSelect('eventTypes', {
    title: 'Booking event type',
    description: 'Filter bookings by event types that occurred during their lifecycle.',
    options: BOOKING_EVENT_TYPE_OPTIONS,
    exclude: true
  }),
  eventSources: bookingFilterUnit.multiSelect('eventSources', {
    title: 'Booking event source',
    description: 'Filter bookings by the source that triggered events during their lifecycle.',
    options: BOOKING_EVENT_SOURCE_OPTIONS,
    exclude: true
  }),
  hasSalesItemChange: bookingFilterUnit.boolean('hasSalesItemChange', {
    title: 'Has product change',
    description: 'Filter bookings that have had a product added, removed, or changed.'
  }),
  hasTimeChange: bookingFilterUnit.boolean('hasTimeChange', {
    title: 'Has time change',
    description: 'Filter bookings that have had a time window change.'
  }),
  hasAdminNote: bookingFilterUnit.boolean('hasAdminNote', {
    title: 'Has admin note',
    description: 'Filter bookings that have an admin note.'
  }),
  hasCustomerNote: bookingFilterUnit.boolean('hasCustomerNote', {
    title: 'Has customer note',
    description: 'Filter bookings that have a customer note.'
  }),
  hasWorkerNote: bookingFilterUnit.boolean('hasWorkerNote', {
    title: 'Has worker note',
    description: 'Filter bookings that have a worker note.'
  })
};

/** Date ranges and on-time flags, in sidebar order: every booking list ends with them. */
export const bookingTimingFilters = [
  bookingFilterUnit.dateRange('startedAt', {
    title: 'Started at',
    description: 'Filter by started at date range',
    exclude: true
  }),
  bookingFilterUnit.dateRange('deliveryWindowStartsAt', {
    title: 'Delivery window start',
    description: 'Filter by delivery window start date range',
    exclude: true
  }),
  bookingFilterUnit.dateRange('deliveryWindowEndsAt', {
    title: 'Delivery window end',
    description: 'Filter by delivery window end date range',
    exclude: true
  }),
  bookingFilterUnit.boolean('startedTooEarly', {
    title: 'Started too early',
    description: 'Started before the delivery window.'
  }),
  bookingFilterUnit.boolean('startedTooLate', {
    title: 'Started too late',
    description: 'Started after the delivery window.'
  }),
  bookingFilterUnit.boolean('endedTooEarly', {
    title: 'Ended too early',
    description: 'Completed before the delivery window started.'
  }),
  bookingFilterUnit.boolean('endedTooLate', {
    title: 'Ended too late',
    description: 'Completed after the delivery window ended.'
  }),
  bookingFilterUnit.dateRange('createdAt', { ...CREATED_AT_FILTER_TEXT, exclude: true }),
  bookingFilterUnit.dateRange('updatedAt', { ...UPDATED_AT_FILTER_TEXT, exclude: true })
] as const;

/** Customer relation filters booking lists have and adapter lists do not (the adapter endpoints lack them). */
export const bookingCustomerRelationStaticFilters = {
  organizationTypes: bookingFilterUnit.multiSelect('organizationTypes', {
    title: 'Organization type',
    description: 'Show bookings whose customer organization is one of these types.',
    options: ORGANIZATION_TYPE_FILTER_OPTIONS,
    exclude: true
  }),
  hasInvoice: bookingFilterUnit.boolean('hasInvoice', {
    title: 'Has invoice',
    description: 'Show bookings whose customer group can pay with invoice (Faktura).'
  }),
  hasDataQualityIssues: bookingFilterUnit.boolean('hasDataQualityIssues', HAS_DATA_QUALITY_ISSUES_FILTER_TEXT),
  dataQualityIssueTypes: bookingFilterUnit.multiSelect('dataQualityIssueTypes', {
    ...DATA_QUALITY_ISSUE_TYPES_FILTER,
    exclude: true
  })
};
